/**
 * Dial bootstrap. Registers components, wires gate -> app.
 */
import './components/gate.ts';
import './components/tier-card.ts';
import './components/warmth-spotlight.ts';
import './components/top-10.ts';
import './components/rank-by-kind.ts';
import './components/cities.ts';
import './components/neglected-alert.ts';
import './components/share-card.ts';
// AAA genius surfaces
import './components/relationship-graph.ts';
import './components/voice-memo.ts';
import './components/gift-log.ts';
import './components/circle-tags.ts';
import './components/connect-section.ts';
import './components/upgrade-card.ts';
import './components/pay-cta.ts';

import { store } from './store.ts';
import { loadSummary } from './storage.ts';
import { decryptJSON } from './crypto.ts';
import { TIER_DISPLAY, TIER_THRESHOLDS_ACTIVE_COUNT, TIERS, daysSince } from './types.ts';
import type { Summary, Tier } from './types.ts';
import './sync/sync-v2.ts';

const TIER_VERDICT: Record<Tier, { promo: string; arc: string }> = {
  loner:          { arc: 'solo → small circle',     promo: 'A loner with leverage. Four real ones, and the room tilts.' },
  small_circle:   { arc: 'small circle → active',   promo: 'Chaar yaar locked. Now widen the dial — the city talks.' },
  active_network: { arc: 'active → wide network',   promo: 'Active. Every shaam ki mehfil has your seat. Compound it.' },
  wide_network:   { arc: 'wide → connector',         promo: 'Wide. Har city mein dost. One more lap and you become the bridge.' },
  connector:      { arc: 'connector — the bridge',   promo: 'Connector. The 47 who’d take your call at midnight — unhi mein duniya hai.' },
};

async function bootstrap(): Promise<void> {
  const cached = await loadSummary();
  if (cached) {
    store.patch({ phase: 'ready', summary: cached });
    revealApp();
    refreshHero(cached);
    return;
  }

  const hashPasscode = location.hash.slice(1).trim();
  const sessionPasscode = sessionStorage.getItem('dial:passcode') ?? '';
  const passcode = hashPasscode || sessionPasscode;
  if (passcode) {
    try {
      const summary = await fetchAndDecrypt(passcode);
      store.patch({ phase: 'ready', summary });
      revealApp();
      refreshHero(summary);
      return;
    } catch {
      // fall through to gate
    }
  }
  store.patch({ phase: 'gate' });
  showGate();
}

async function fetchAndDecrypt(passcode: string): Promise<Summary> {
  const r = await fetch('/data.encrypted.json', { cache: 'no-cache' });
  if (!r.ok) throw new Error('fetch failed');
  const body = await r.json() as { ciphertext: string };
  return decryptJSON<Summary>(passcode, body.ciphertext);
}

function showGate(): void {
  const gate = document.querySelector('dial-gate');
  const app = document.getElementById('app');
  if (gate) gate.removeAttribute('hidden');
  if (app) app.hidden = true;
}

function revealApp(): void {
  const gate = document.querySelector('dial-gate');
  const app = document.getElementById('app');
  if (gate) gate.setAttribute('hidden', '');
  if (app) app.hidden = false;
}

function refreshHero(s: Summary): void {
  const headline = document.getElementById('headline');
  const sub = document.getElementById('hero-sub');
  if (headline) headline.textContent = TIER_DISPLAY[s.tier].name;
  if (sub) {
    const verdict = TIER_VERDICT[s.tier];
    const idx = TIERS.indexOf(s.tier);
    const next: Tier | null = idx >= 0 && idx < TIERS.length - 1 ? (TIERS[idx + 1] as Tier) : null;
    const toNext = next != null
      ? Math.max(0, TIER_THRESHOLDS_ACTIVE_COUNT[next] - s.active_connection_count)
      : 0;
    const tail = next != null && toNext > 0
      ? `· ${toNext} more to ${TIER_DISPLAY[next].name.toLowerCase()}`
      : '· you are the bridge';
    sub.textContent = `${s.active_connection_count} active · ${s.inner_circle.length} in circle · ${verdict.arc} ${tail}`;
  }
  refreshPromo(s);
  refreshMarginalia(s);
}

function refreshPromo(s: Summary): void {
  const promo = document.getElementById('hero-promo');
  const line = document.getElementById('hero-promo-line');
  if (!promo || !line) return;
  const verdict = TIER_VERDICT[s.tier];
  const idx = TIERS.indexOf(s.tier);
  const next: Tier | null = idx >= 0 && idx < TIERS.length - 1 ? (TIERS[idx + 1] as Tier) : null;
  const arcText = next != null
    ? `${TIER_DISPLAY[s.tier].name} → ${TIER_DISPLAY[next].name}`
    : `${TIER_DISPLAY[s.tier].name} · arrival`;
  const arcEl = promo.querySelector('.promo-arc');
  if (arcEl) arcEl.textContent = arcText;
  line.textContent = verdict.promo;
  promo.removeAttribute('hidden');
}

function refreshMarginalia(s: Summary): void {
  const aside = document.getElementById('marginalia-neglected');
  const text = document.getElementById('marginalia-neglected-text');
  if (!aside || !text) return;

  const neglected = s.insights.most_neglected;
  if (!neglected) {
    aside.setAttribute('hidden', '');
    return;
  }

  const days = daysSince(neglected.last_contact_iso);
  if (days == null || days < 30) {
    aside.setAttribute('hidden', '');
    return;
  }

  const first = neglected.name.split(/\s+/)[0] ?? neglected.name;
  text.innerHTML = `“${escapeHtml(first)}” hasn't picked up in <strong>${days}</strong> days. Call them.`;
  aside.removeAttribute('hidden');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

store.subscribe((s) => {
  if (s.phase === 'ready' && s.summary) {
    revealApp();
    refreshHero(s.summary);
  }
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* ignore */ });
  });
}

bootstrap();
import { mountAmbient as mountEmpireSelf } from './agent/hook.ts';
queueMicrotask(() => mountEmpireSelf());
